import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useContext,
  ChangeEvent,
  FC,
}                           from 'react';
import { Breakpoint }       from '@material-ui/core/styles/createBreakpoints';
import { IAuctionDetails }  from '@typings/auction';
import { ICreditUnion }     from '@typings/accountManagement';
import { EAppPage }         from '@enums/common';
import {
  EBodyStyle,
  EAudience,
  EAuctionStatus,
  EAuctionsPage,
}                           from '@enums/auction';
import {
  useUpdateAuction,
  useFetchAuctionData,
}                           from '@modules/auctions/queries/auctionsQueries';
import {
  IVinDecodeResult,
  useDecodeVinCode,
}                           from '@utils/useDecodeVinCode';
import { FormikRefContext } from '@utils/FormikRefContext';
import { auctionSchema }    from '@modules/accountManagement/utils/auctionValidationSchema';
import {
  AuctionDetailsPage,
  IVehicleInfoValues,
}                           from '@modules/accountManagement/pages/AuctionDetailsPage';
import { Loader }           from '@components/Loader/Loader';

interface IProps {
  push           : (path: string) => void;
  width          : Breakpoint;
  auctionId      : string;
  currentAccount : ICreditUnion;
}

const getInitialValues = (
  auction: IAuctionDetails | null,
): IVehicleInfoValues => ({
  vin                          : auction?.vehicle?.vin ?? '',
  vehicleBodyStyle             : auction?.vehicle?.vehicleBodyStyle ?? EBodyStyle.Car,
  year                         : auction?.vehicle?.year ?? 0,
  carBrandName                 : auction?.vehicle?.carBrandName ?? '',
  carModelName                 : auction?.vehicle?.carModelName ?? '',
  miles                        : auction?.vehicle?.miles ?? 0,
  vehicleDescription           : auction?.vehicle?.vehicleDescription ?? '',
  featuresAndEquipment         : auction?.vehicle?.featuresAndEquipment ?? '',
  fullDisclosure               : auction?.vehicle?.fullDisclosure ?? '',
  exteriorCondition            : auction?.vehicle?.exteriorCondition ?? 2,
  interiorCondition            : auction?.vehicle?.interiorCondition ?? 2,
  mechanicalCondition          : auction?.vehicle?.mechanicalCondition ?? 2,
  locationId                   : auction?.locationId ?? '',
  inventoryContactId           : auction?.inventoryContactId ?? '',
  minimumBid                   : auction?.minimumBid ?? 0,
  bidIncrement                 : auction?.bidIncrement ?? 0,
  retailValue                  : auction?.retailValue ?? 0,
  wholesaleValue               : auction?.wholesaleValue ?? 0,
  paymentTerms                 : auction?.paymentTerms ?? '',
  includeSpecialFinancingOffer : !!auction?.specialFinancingOffer,
  specialFinancingOffer        : auction?.specialFinancingOffer ?? '',
  startDateAndTime             : auction?.startDate ?? '',
  endDateAndTime               : auction?.endDate ?? '',
  audience                     : auction?.audience ?? EAudience.DealerAndPublic,
});

const DRAFT_STATUSES: EAuctionStatus[] = [
  EAuctionStatus.SavedDraft,
  EAuctionStatus.SavedDraftNoPhotos,
];

export const AuctionDetailsContainer: FC<IProps> = ({
  push,
  width,
  auctionId,
  currentAccount,
}) => {
  const [auction, setAuction] = useState<IAuctionDetails | null>(null);

  const formikRef = useContext(FormikRefContext);

  const onSuccessDecodeVin = useCallback(
    ({
      carBrandName : make,
      carModelName : model,
      vehicleBodyStyle,
      year,
    }: IVinDecodeResult): void => {
      if (!formikRef.current) {
        return;
      }

      const { values, setValues } = formikRef.current;

      setValues({
        ...values,
        vehicle: {
          ...values.vehicle,
          year,
          make,
          model,
          vehicleBodyStyle,
        },
      });
    },
    [formikRef],
  );

  const onErrorDecodeVin = useCallback(
    () => {
      if (!formikRef.current) {
        return;
      }

      const { values, setValues } = formikRef.current;

      setValues({
        ...values,
        vehicle: {
          ...values.vehicle,
          vin   : values.vehicle?.vin,
          make  : '',
          model : '',
          year  : null,
          miles : '',
        },
      });
    },
    [formikRef],
  );

  const { onInputVinCode } = useDecodeVinCode(
    onSuccessDecodeVin,
    onErrorDecodeVin,
  );

  const vinInputHandler = useCallback(
    async (event : ChangeEvent<HTMLInputElement>) => {
      if (!auction?.cuid) return;

      await onInputVinCode(event, auction.cuid);
    },
    [onInputVinCode, auction],
  );

  // #region API INTEGRATION
  const {
    refetch,
    isLoading,
  } = useFetchAuctionData(
    auctionId,
    {
      enabled   : !!auctionId,
      onSuccess : (data) => {
        setAuction(data);
      },
    },
  );

  const {
    mutateAsync : updateAuction,
    isLoading   : isLoadingUpdate,
  } = useUpdateAuction({
    onSuccess: () => {
      formikRef.current.resetForm();
      push(`${EAppPage.Auctions}/${EAuctionsPage.Search}`);
    },
  });
  // #endregion

  useEffect(
    (): any => () => { setAuction(null); },
    [auctionId, refetch],
  );

  const initialValues = useMemo(
    (): IVehicleInfoValues => getInitialValues(auction),
    [auction],
  );

  const isDraft = useMemo(
    () => !!auction && DRAFT_STATUSES.includes(auction.status),
    [auction],
  );

  const handleUpdateAuction = useCallback(
    async (values: IVehicleInfoValues): Promise<void> => {
      if (!auction) return;

      const updatedValues: IAuctionDetails = {
        ...auction,
        vehicle: {
          vin                  : values.vin,
          vehicleBodyStyle     : values.vehicleBodyStyle,
          year                 : values.year,
          miles                : values.miles,
          carBrandName         : values.carBrandName,
          carModelName         : values.carModelName,
          vehicleDescription   : values.vehicleDescription,
          featuresAndEquipment : values.featuresAndEquipment,
          fullDisclosure       : values.fullDisclosure,
          exteriorCondition    : values.exteriorCondition,
          interiorCondition    : values.interiorCondition,
          mechanicalCondition  : values.mechanicalCondition,
        },
        isDraft,
      };

      if (auctionId) {
        updatedValues.id = auctionId;
      }

      await updateAuction(updatedValues);
    },
    [auctionId, updateAuction, isDraft, auction],
  );

  if (isLoading) {
    return (<Loader />);
  }

  return (
    <AuctionDetailsPage
      push                = {push}
      width               = {width}
      inventoryContacts   = {[...(auction?.inventoryContact ? [auction?.inventoryContact] : [])]}
      locations           = {[...(auction?.location ? [auction?.location] : [])]}
      currentAccount      = {currentAccount}
      initialValues       = {initialValues}
      validationSchema    = {auctionSchema}
      onInputVinCode      = {vinInputHandler}
      formikRef           = {formikRef}
      handleUpdateAuction = {handleUpdateAuction}
      isLoadingUpdate     = {isLoadingUpdate}
      carfaxReport        = {auction?.carFaxReport}
    />
  );
};
